import { usePortfolioSelectedOrders } from 'portfolio/store/hooks';
import { safeCalculatePercent } from 'utils/percent';
import {
    isChildOrder,
    isNotCancelledOrRejected,
    OpenSubmissionsStreamItem,
} from './store/stream/OpenSubmissionsStreamDataState';
import Order from 'communication/types/order/Order';
import {
    isAlternativeOrPeriodic,
    isConditional,
    isDark,
    isLitOrExchange,
    isNotConditional,
    isOther,
    isSiOrSdp,
    VenueType,
} from 'utils/venueTypeUtils';

interface AggregatedParentOrders {
    filled: number;
    totalQuantity: number;
    totalPrice: number;
}

const aggregateParentOrders = (orders: Order[]): AggregatedParentOrders => {
    const aggregatedParentOrders = {
        filled: 0,
        totalQuantity: 0,
        totalPrice: 0,
    };
    orders
        .map((o) => ({
            filled: o.fillQty,
            totalQuantity: o.qty,
            totalPrice: o.fillQty * o.avgPx!,
        }))
        .forEach((item) =>
            Object.keys(item).forEach((k) => {
                const key = k as keyof AggregatedParentOrders;
                aggregatedParentOrders[key] += item[key];
            })
        );
    return aggregatedParentOrders;
};

interface AggregatedChildOrders {
    litOrExchange: number;
    dark: number;
    siOrSdp: number;
    alternativeOrPeriodic: number;
    other: number;
    conditional: number;
    notConditional: number;
}

const getRemainingQuantity = (
    d: OpenSubmissionsStreamItem,
    isApplicableForType: (type: VenueType) => boolean
) => (isApplicableForType(d.executionVenueType) ? d.remainingQty : 0);

const aggregateChildOrders = (
    data: OpenSubmissionsStreamItem[]
): AggregatedChildOrders => {
    const aggregated: AggregatedChildOrders = {
        litOrExchange: 0,
        dark: 0,
        siOrSdp: 0,
        alternativeOrPeriodic: 0,
        other: 0,
        conditional: 0,
        notConditional: 0,
    };
    data.filter(isChildOrder)
        .filter(isNotCancelledOrRejected)
        .map((d) => ({
            litOrExchange: getRemainingQuantity(d, isLitOrExchange),
            dark: getRemainingQuantity(d, isDark),
            siOrSdp: getRemainingQuantity(d, isSiOrSdp),
            other: getRemainingQuantity(d, isOther),
            alternativeOrPeriodic: getRemainingQuantity(
                d,
                isAlternativeOrPeriodic
            ),
            conditional: getRemainingQuantity(d, isConditional),
            notConditional: getRemainingQuantity(d, isNotConditional),
        }))
        .forEach((item) =>
            Object.keys(item).forEach((k) => {
                const key = k as keyof AggregatedChildOrders;
                aggregated[key] += item[key];
            })
        );
    return aggregated;
};

const calculateAverageExecutionPrice = (
    orders: Order[],
    aggregated: AggregatedParentOrders
): number => {
    if (orders.length === 1) {
        return orders[0].avgPx!;
    } else if (!aggregated.filled) {
        return 0;
    } else {
        return aggregated.totalPrice / aggregated.filled;
    }
};

export interface AggregatedOpenSubmissions {
    filled: number;
    litOrExchange: number;
    siOrSdp: number;
    dark: number;
    alternativeOrPeriodic: number;
    other: number;
    conditional: number;
    notConditional: number;
    remaining: number;
    averageExecutionPrice: number;
    percentageComplete: number;
    totalQuantity: number;
}

export const useAggregatedOpenSubmissions = (
    items: OpenSubmissionsStreamItem[]
): AggregatedOpenSubmissions => {
    const selectedOrders = usePortfolioSelectedOrders();
    const aggregatedParentOrders = aggregateParentOrders(selectedOrders);
    const aggregatedChildOrders = aggregateChildOrders(items);
    const averageExecutionPrice = calculateAverageExecutionPrice(
        selectedOrders,
        aggregatedParentOrders
    );
    return {
        ...aggregatedChildOrders,
        conditional: Math.min(
            aggregatedChildOrders.conditional,
            aggregatedParentOrders.totalQuantity -
                aggregatedParentOrders.filled -
                aggregatedChildOrders.litOrExchange -
                aggregatedChildOrders.dark -
                aggregatedChildOrders.other -
                aggregatedChildOrders.siOrSdp -
                aggregatedChildOrders.alternativeOrPeriodic
        ),
        remaining: Math.max(
            0,
            aggregatedParentOrders.totalQuantity -
                aggregatedParentOrders.filled -
                aggregatedChildOrders.litOrExchange -
                aggregatedChildOrders.dark -
                aggregatedChildOrders.other -
                aggregatedChildOrders.conditional -
                aggregatedChildOrders.siOrSdp -
                aggregatedChildOrders.alternativeOrPeriodic
        ),
        filled: aggregatedParentOrders.filled,
        averageExecutionPrice,
        percentageComplete: safeCalculatePercent(
            aggregatedParentOrders.filled,
            aggregatedParentOrders.totalQuantity
        ),
        totalQuantity: aggregatedParentOrders.totalQuantity,
    };
};
